/**
 * components/print/InvoicePusat.js
 * Invoice Pusat — the full-form document for a PUSAT-anchored PENJUALAN or a
 * PUSAT_TO_CABANG TRANSFER (PRD §5.2). Full-screen print overlay, same "print only this
 * element" pattern `pages/PrintInvoicePilot.js` established, now wired to REAL transaction
 * data instead of hardcoded sample data. Opens directly (no print dialog first) — this
 * document collects no colly data at all (PRD §6: the pusat invoice has no COLY column),
 * unlike the Checklist/Invoice Cabang/Surat Jalan documents (T2c-2/T2d).
 *
 * Multi-page pagination (running subtotal) is delegated entirely to `utils/pagination.js`'s
 * `paginateLines` — this component only renders what that function returns; it never
 * re-derives a page split or a cumulative total itself, so its arithmetic can't drift from
 * the engine's own (independently unit-tested) guarantees.
 *
 * **Phase T2c-1-fix**: corrected against the client's real invoice sample —
 *  - Filler rows are now REAL empty `<tr>`s rendered BEFORE the TOTAL row (one per unused
 *    row slot up to ROWS_PER_PAGE_PUSAT), not a single CSS "auto-stretch" row. Verified via a
 *    real headless-Chrome print-media render that a `table-layout:fixed` row with no explicit
 *    height does NOT absorb a table's leftover `height:100%` — browsers only distribute extra
 *    table height across rows for auto (non-fixed) table layouts, never fixed ones. An empty
 *    `<td>` still reserves a normal single line-box (confirmed the same way), so the filler
 *    rows render at the same height as a real data row — the ruled grid visually continues
 *    down the page instead of collapsing.
 *  - The header is now two side-by-side columns (company block + Invoice line on the left,
 *    "Nama Pembeli" + buyer block + date on the right) instead of a stacked "Kepada Yth:"
 *    block — see the CSS-flex header block below.
 *  - The date uses `fmtDateLong` (full Indonesian month name, no label) instead of
 *    `fmtDateShort` with a "Tanggal :" label.
 *  - Harga/Jumlah are now TWO real table columns each (a narrow "Rp" cell + a right-aligned
 *    amount cell), not one column with an inline "Rp" prefix — matching the reference
 *    sample's ruled sub-column. The TOTAL/carry rows collapse their label across the first
 *    six columns, then show "Rp" and the amount in their own cells, reading
 *    "TOTAL | Rp | 3.176.000".
 *
 * **PPN note**: a fixed, always-shown "Harga sudah termasuk PPN" line renders between the item
 * table and the footer box — left-aligned, never conditional, Invoice Pusat only (no other
 * print document gets it). Inside the per-page loop, same as the footer itself, so it repeats
 * on every physical page of a multi-page invoice, not just the last one.
 *
 * Footer box (below the TOTAL row): PRINTED STRUCTURE, almost entirely blank on purpose —
 * the shop's own physical rubber stamp already carries bank/account details, and signatures
 * are handwritten. This prints ONLY the box border and two labels ("Diterima," bottom-left,
 * "Hormat Kami," bottom-right) — never an account number, a signature, or the company stamp.
 * Anchored to the bottom of the page via `margin-top: auto` on `.print-doc__footer` (defense
 * in depth alongside the filler rows themselves — see printFoundation.css).
 *
 * **Phase T2c-1-fix-2 — rendered via `ReactDOM.createPortal` into `document.body`, NOT
 * inline in the component tree**: found (via a real CDP `Page.printToPDF` render — the same
 * engine behind the browser's native Print dialog, not just `emulateMediaType`) that the
 * printed page was coming out at US Letter (8.5in×11in), silently ignoring the `@page pusat
 * { size: 9.5in 11in; }` rule entirely — which meant the content, sized for a genuine 8.5in
 * PRINTABLE AREA (i.e. assuming @page's own margins already carved out of a 9.5in sheet), was
 * being asked to fit inside a narrower area again reduced by margins on an 8.5in Letter page,
 * causing the reported horizontal-scrollbar overflow in the print dialog. Root cause: this
 * component used to render inline inside the app's normal React tree (a descendant of
 * `#root`, alongside the sidebar/current page), hidden via `visibility:hidden` — but
 * `visibility:hidden` elements still occupy real layout space and still fragment into their
 * own print "page," which (having no `page:` name assigned) falls back to Chromium's DEFAULT
 * page size. That default-sized fragment, appearing in the same document as the invoice's own
 * named-page content, was overriding the whole print job's detected page size — confirmed by
 * isolated reproduction: identical CSS with NO other hidden content sizes correctly at 9.5×11,
 * but the moment ANY other in-flow (even invisible) content shares the document, it reverts to
 * 8.5×11. A portal renders this overlay as a SIBLING of `#root`, not a descendant, so
 * `@media print { #root { display: none; } }` (printFoundation.css) can remove the entire rest
 * of the app from layout with a real `display:none` (which — unlike `visibility:hidden` —
 * generates no box and no page fragment at all), leaving this document as the ONLY
 * page-generating content when printing. The old `visibility:hidden`-cascade / "print only
 * this element" hack (inherited from the throwaway `pages/PrintInvoicePilot.js` pilot) is
 * gone from printFoundation.css for exactly this reason — it was never wrong in isolation, but
 * combined with a same-tree overlay it was the actual mechanism behind this bug.
 *
 * **Phase T2-settings**: the letterhead now comes from the `company` prop (App.js passes
 * `data.settings.company`, edited via the Pengaturan modal) instead of importing
 * `utils/company.js`'s `IJM_COMPANY` directly — that constant is now only the DEFAULT value
 * the storage migration seeds on first upgrade (and this component's own fallback, below, for
 * any caller that doesn't pass one). This is NOT per-invoice data: unlike `txn.buyerBlock`
 * (still read straight off the transaction, unchanged), the company block is the same on every
 * document regardless of which transaction is being printed — there is no per-invoice company
 * override anywhere in this file.
 *
 * **Print settings reminder**: the overlay action bar shows `PRINT_SETTINGS_REMINDER_PUSAT`
 * (`utils/pagination.js` — the one module every print document already imports) right before
 * the Cetak button. Chrome's print dialog remembers only ONE paper-size/margin configuration
 * per origin, shared by all five documents — this reminder exists because staff regularly
 * switch between this document (needs Letter/Letter Fanfold) and the other four (need
 * Statement) throughout the day, and whichever was picked last silently carries over,
 * reproducing the phantom-extra-page overflow bug. Not fixable in app code; see
 * `printFoundation.css`'s own header comment for that bug's history.
 *
 * **Address overrides for printing**: a screen-only editor sits to the LEFT of the document,
 * side by side (`.print-doc-overlay__body`, a flex row wrapping both
 * `.print-doc-overlay__override-panel` and `.print-doc` — not stacked above it, so the document
 * is the first thing visible when the overlay opens; below a narrow-viewport breakpoint it
 * drops back to a stacked column, see printFoundation.css's own comment). Lets staff correct
 * the Pusat letterhead
 * address and/or the buyer address for THIS print only — never writing back to
 * `settings.company` or the Contact record. Defaults come from the FROZEN
 * `txn.buyerBlock.address` (never a live Contact lookup — a transaction is a historical
 * record, same as `location`/`branchId`/invoice numbers) and the live `company.addressLines`
 * (shop-wide, not per-transaction, same as every other reader of `company`). Persists to
 * `txn.addressOverrides.invoicePusat` only when Cetak is clicked (never on every keystroke —
 * closing via Tutup discards an in-progress edit); a blank required field disables Cetak. This
 * is a UI-only guard on the in-app Cetak button — it cannot intercept a native Ctrl+P shortcut,
 * a known, unavoidable limitation of any client-side print gate.
 */
import React, { useState } from "react";
import { createPortal } from "react-dom";
import "./printFoundation.css";
import { paginateLines, NBSP, PRINT_SETTINGS_REMINDER_PUSAT } from "../../utils/pagination";
import { IJM_COMPANY } from "../../utils/company";
import { fmtQty, fmtDateLong } from "../../utils/idGenerators";
import AddressOverrideField from "./AddressOverrideField";

// named constant — tuned against real print output in T2e.
//
// pusat-header-align-fix session -- LOWERED 20 -> 17, real-Chromium-measured, after the header
// restructure below (row1/row2) added a genuinely new, unconditional line to every page. The
// naive prediction (one new ~29px row2 line costs about one table row) undershot badly: real
// page.pdf() measurement showed 20, 19, AND 18 all still overflow a page-1-of-multipage case
// (20-row/19-row/18-row page + Hal, since Hal renders on EVERY page once multiPage is true, not
// just continuation pages) to a real 2nd physical page -- confirmed independently via a
// worst-case page-2 test (N rows + the carry-in row) at each candidate, all consistent. 17 is
// the first value where BOTH the page-1 case (N+1 lines) and the page-2 case (2xN lines,
// forcing a full carry-laden last page) land at exactly 2 real physical pages, never 3. At 17,
// a full zero-wrap page lands at 979.1875px against the SAME 979.2px/10.2in ceiling this
// constant has been measured against since T2c-1-fix -- an identical 0.0125px margin to the
// OLD 20-row/single-row-header measurement, not a coincidence: the new header costs almost
// exactly 3 table rows' worth of vertical space (mostly row1-right's Hal-adjacent-group height
// plus the fully separate row2 line), and 17 is where that cost is paid back in full. The
// established "safe for at most one realistic wrap, not two" bar (see ROWS_PER_PAGE_CABANG's
// own history) was RE-VERIFIED at 17, not assumed to carry over: one genuinely long (81-char)
// wrapped item name among the 17 still lands at the identical 979.1875px/0.0125px margin (zero
// cost -- absorbed by the page's own filler-row slack), while two such names on the same page
// genuinely overflow to a real second page, exactly matching the old N=20 bar's own shape.
// Verified via a temporary puppeteer-core+pdf-lib script against the live dev server (removed
// after this session, never committed) -- both getBoundingClientRect() under
// emulateMediaType('print') and independent page.pdf({preferCSSPageSize:true}) real page
// counts agree at every candidate tested (20, 19, 18, 17).
const ROWS_PER_PAGE_PUSAT = 17;

// General-purpose 1..n -> Roman numeral for the "Hal I"/"Hal II" page marker (PRD §5.2).
// Deliberately NOT `invoiceNumbers.js`'s `romanMonth` — that lookup table is capped at 12
// (months only); a real transaction could in theory span more pages than that.
const ROMAN_VALUES = [
  [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"],
  [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
];
function toRoman(n) {
  let num = n;
  let out = "";
  for (const [value, symbol] of ROMAN_VALUES) {
    while (num >= value) {
      out += symbol;
      num -= value;
    }
  }
  return out;
}

export default function InvoicePusat({ txn, items = [], company = IJM_COMPANY, onClose, onSaveAddressOverride }) {
  const byCode = new Map(items.map((it) => [it.internalCode, it]));
  const { pages } = paginateLines(txn.lines || [], ROWS_PER_PAGE_PUSAT);
  const savedOverride = txn.addressOverrides?.invoicePusat;
  const [pusatAddressText, setPusatAddressText] = useState(
    () => (savedOverride?.pusatAddressLines || company.addressLines || []).join("\n")
  );
  const [buyerAddressOverride, setBuyerAddressOverride] = useState(
    () => savedOverride?.buyerAddress ?? (txn.buyerBlock?.address || "")
  );
  const buyer = txn.buyerBlock || { name: "", address: "" };
  const multiPage = pages.length > 1;
  const canPrint = pusatAddressText.trim() !== "" && buyerAddressOverride.trim() !== "";
  const handleCetak = () => {
    if (!canPrint) return;
    onSaveAddressOverride?.(txn.id, "invoicePusat", {
      pusatAddressLines: pusatAddressText.split("\n").map((l) => l.trim()).filter((l) => l !== ""),
      buyerAddress: buyerAddressOverride.trim(),
    });
    window.print();
  };

  return createPortal(
    <div className="print-doc-overlay">
      <div className="print-doc-overlay__actions">
        <span className="print-doc-overlay__note">Invoice Pusat — {txn.invoiceNumber || txn.id}</span>
        <span className="print-doc-overlay__reminder">{PRINT_SETTINGS_REMINDER_PUSAT}</span>
        <button type="button" className="btn" onClick={onClose}>Tutup</button>
        <button type="button" className="btn btn-primary" onClick={handleCetak} disabled={!canPrint}>Cetak</button>
      </div>

      <div className="print-doc-overlay__body">
        <div className="print-doc-overlay__override-panel">
          <AddressOverrideField label="Alamat Pusat" value={pusatAddressText} onChange={setPusatAddressText} rows={3} />
          <AddressOverrideField label="Alamat Pembeli" value={buyerAddressOverride} onChange={setBuyerAddressOverride} rows={2} />
        </div>

        <div className="print-doc print-doc--pusat">
        {pages.map((page) => {
          const fillerCount = Math.max(0, ROWS_PER_PAGE_PUSAT - page.lines.length);
          return (
            <div className="print-page" key={page.pageNumber}>
              <div className="print-doc__header print-doc__header--stacked">
                <div className="print-doc__header-row1">
                  <div className="print-doc__header-col">
                    <div className="print-doc__title">{company.name}</div>
                    {pusatAddressText.split("\n").map((line, i) => (
                      <div key={i} className="print-doc__addr">{line}</div>
                    ))}
                    <div className="print-doc__addr">{company.npwp}</div>
                  </div>
                  <div className="print-doc__header-row1-right">
                    {multiPage && <div className="print-doc__hal">Hal {toRoman(page.pageNumber)}</div>}
                    <div className="print-doc__header-col print-doc__header-col--right">
                      <div className="print-doc__label">Nama Pembeli</div>
                      <div>{buyer.name}</div>
                      {buyerAddressOverride.split("\n").map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="print-doc__header-row2">
                  <div className="print-doc__header-invoice">
                    <span className="print-doc__label">Invoice : </span>{txn.invoiceNumber}
                  </div>
                  <div className="print-doc__header-date">{fmtDateLong(txn.date)}</div>
                </div>
              </div>

              <hr className="print-doc__hr" />

              <div className="print-doc__table-area">
                <table className="print-doc__table">
                  <colgroup>
                    <col style={{ width: "0.4in" }} />
                    <col style={{ width: "1.3in" }} />
                    <col />
                    <col style={{ width: "0.9in" }} />
                    <col style={{ width: "0.35in" }} />
                    <col style={{ width: "1.0in" }} />
                    <col style={{ width: "0.35in" }} />
                    <col style={{ width: "1.1in" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Kode Barang</th>
                      <th>Nama Barang</th>
                      <th className="num">Quantity</th>
                      <th colSpan={2}>Harga</th>
                      <th colSpan={2}>Jumlah</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.pageNumber > 1 && (
                      <tr className="print-doc__carry-row">
                        <td colSpan={6} style={{ textAlign: "right" }}>
                          TOTAL HAL {toRoman(page.pageNumber - 1)} SEBELUMNYA
                        </td>
                        <td className="print-doc__rp">Rp</td>
                        <td className="num">{fmtQty(page.carryIn)}</td>
                      </tr>
                    )}
                    {page.lines.map((l, i) => {
                      const rowNo = (page.pageNumber - 1) * ROWS_PER_PAGE_PUSAT + i + 1;
                      const name = l.name || byCode.get(l.internalCode)?.name || l.internalCode;
                      return (
                        <tr key={i}>
                          <td>{rowNo}</td>
                          <td>{l.internalCode}</td>
                          <td className="wrap">{name}</td>
                          <td className="num">{fmtQty(l.qty)} PCS</td>
                          <td className="print-doc__rp">Rp</td>
                          <td className="num">{fmtQty(l.unitPrice)}</td>
                          <td className="print-doc__rp">Rp</td>
                          <td className="num">{fmtQty(l.qty * l.unitPrice)}</td>
                        </tr>
                      );
                    })}
                    {Array.from({ length: fillerCount }).map((_, i) => (
                      // A truly empty <td> collapses to a much shorter line box than one with
                      // text (confirmed via a real headless-Chrome print-media render: ~7px
                      // vs a real data row's ~27px) — NBSP (utils/pagination.js) keeps every
                      // filler row the SAME height as a real item row, so the ruled grid reads
                      // as one uniform continuous table, not a mix of tall and short rows. Use
                      // the shared constant, never a re-typed literal — an ORDINARY space looks
                      // identical in an editor but is CSS-collapsible and silently reproduces
                      // this exact bug (confirmed the hard way in Phase T2c-2-fix-2, where two
                      // other documents had copied this technique with a plain space instead).
                      <tr className="print-doc__filler" key={`filler-${i}`}>
                        <td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td>
                        <td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td><td>{NBSP}</td>
                      </tr>
                    ))}
                    <tr className="print-doc__total-row">
                      <td colSpan={6} style={{ textAlign: "right" }}>TOTAL</td>
                      <td className="print-doc__rp">Rp</td>
                      <td className="num">{fmtQty(page.pageTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="print-doc__ppn-note">Harga sudah termasuk PPN</div>

              <div className="print-doc__footer">
                <div className="print-doc__footer-cell">
                  <div className="print-doc__footer-label">Diterima,</div>
                  <div className="print-doc__footer-line" />
                </div>
                <div className="print-doc__footer-cell print-doc__footer-cell--center" />
                <div className="print-doc__footer-cell" style={{ alignItems: "flex-end", textAlign: "right" }}>
                  <div className="print-doc__footer-label">Hormat Kami,</div>
                  <div className="print-doc__footer-line" />
                </div>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>,
    document.body
  );
}
